import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
} from '@backstage/ui';
import type { TrackDefinition } from '@internal/backstage-plugin-pulse-common';

export type TrackDeleteDialogProps = {
  track: TrackDefinition;
  isOpen: boolean;
  onClose: () => void;
  remove: (trackId: string) => Promise<void>;
  deleting: boolean;
  onDeleted?: () => void;
};

/** Confirmation dialog for deleting a UI-managed track. */
export const TrackDeleteDialog = ({
  track,
  isOpen,
  onClose,
  remove,
  deleting,
  onDeleted,
}: TrackDeleteDialogProps) => {
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleClose = () => {
    setDeleteError(null);
    onClose();
  };

  const handleDelete = async () => {
    setDeleteError(null);
    try {
      await remove(track.id);
      onClose();
      onDeleted?.();
    } catch (error_) {
      setDeleteError((error_ as Error).message);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) {
          handleClose();
        }
      }}
      width={480}
    >
      <DialogHeader>Delete track</DialogHeader>
      <DialogBody>
        <Flex direction="column" gap="2">
          <Text variant="body-medium" color="secondary">
            {`Delete track "${track.name}"? Its definition and certification history will be removed.`}
          </Text>
          {deleteError ? (
            <Text variant="body-small" color="danger">
              {deleteError}
            </Text>
          ) : null}
        </Flex>
      </DialogBody>
      <DialogFooter>
        <Flex justify="end" gap="2">
          <Button
            variant="secondary"
            isDisabled={deleting}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            destructive
            isPending={deleting}
            isDisabled={deleting}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Flex>
      </DialogFooter>
    </Dialog>
  );
};
